import { useEffect, useRef, useState } from "react";

const SOURCE_POLL_MS = 100;
const SOURCE_POLL_LIMIT = 50;
const audioGraphs = new WeakMap();

function getAgentAudioElement(call) {
  const session = call?.rawSession;
  if (!session) return null;

  // ultravox-client 0.5.0 owns this element but does not expose it publicly.
  // Keep the compatibility access isolated here until voice-sdk provides a
  // supported output-audio hook.
  return session.audioElement instanceof HTMLAudioElement
    ? session.audioElement
    : null;
}

export default function useAgentAudioLevel({
  call,
  audioContext,
  enabled,
  active,
  debug = false,
}) {
  const levelRef = useRef(0);
  const activeRef = useRef(active);
  const [diagnostics, setDiagnostics] = useState({
    source: "idle",
    level: 0,
    error: null,
  });

  useEffect(() => {
    activeRef.current = active;
    if (!active) levelRef.current = 0;
  }, [active]);

  useEffect(() => {
    if (!enabled || !call || !audioContext) {
      levelRef.current = 0;
      setDiagnostics((current) => (
        current.source === "idle" && current.level === 0
          ? current
          : { source: "idle", level: 0, error: null }
      ));
      return undefined;
    }

    let cancelled = false;
    let animationFrame = 0;
    let pollTimer = 0;
    let pollCount = 0;
    let analyser = null;
    let samples = null;
    let lastDebugUpdate = 0;

    const stop = () => {
      cancelled = true;
      window.clearTimeout(pollTimer);
      window.cancelAnimationFrame(animationFrame);
      levelRef.current = 0;
    };

    const readLevel = (timestamp) => {
      if (cancelled || !analyser || !samples) return;

      analyser.getByteTimeDomainData(samples);
      let sumSquares = 0;
      for (let index = 0; index < samples.length; index += 1) {
        const centered = (samples[index] - 128) / 128;
        sumSquares += centered * centered;
      }

      const rms = Math.sqrt(sumSquares / samples.length);
      const target = activeRef.current
        ? Math.min(1, Math.max(0, (rms - 0.012) * 8.5))
        : 0;
      const smoothing = target > levelRef.current ? 0.42 : 0.18;
      levelRef.current += (target - levelRef.current) * smoothing;

      if (debug && timestamp - lastDebugUpdate > 250) {
        lastDebugUpdate = timestamp;
        setDiagnostics({
          source: "ultravox-output",
          level: Number(levelRef.current.toFixed(3)),
          error: null,
        });
      }

      animationFrame = window.requestAnimationFrame(readLevel);
    };

    const attach = async () => {
      if (cancelled) return;
      const audioElement = getAgentAudioElement(call);
      if (!audioElement) {
        pollCount += 1;
        if (pollCount < SOURCE_POLL_LIMIT) {
          pollTimer = window.setTimeout(attach, SOURCE_POLL_MS);
        } else {
          setDiagnostics({
            source: "status-fallback",
            level: 0,
            error: "Ultravox output element unavailable",
          });
        }
        return;
      }

      try {
        if (audioContext.state === "suspended") {
          await audioContext.resume();
        }
        if (cancelled) return;

        const existingGraph = audioGraphs.get(audioElement);
        if (existingGraph) {
          analyser = existingGraph.analyser;
        } else {
          const sourceNode = audioContext.createMediaElementSource(audioElement);
          analyser = audioContext.createAnalyser();
          analyser.fftSize = 512;
          analyser.smoothingTimeConstant = 0.2;
          sourceNode.connect(analyser);
          analyser.connect(audioContext.destination);
          audioGraphs.set(audioElement, { sourceNode, analyser });
        }
        samples = new Uint8Array(analyser.fftSize);

        setDiagnostics({
          source: "ultravox-output",
          level: 0,
          error: null,
        });
        animationFrame = window.requestAnimationFrame(readLevel);
      } catch (error) {
        setDiagnostics({
          source: "status-fallback",
          level: 0,
          error: error instanceof Error ? error.message : String(error),
        });
      }
    };

    void attach();
    return stop;
  }, [audioContext, call, debug, enabled]);

  return { levelRef, diagnostics };
}
